import { fetchJSON as fetchAPI } from './client';

export type SubscriptionRecord = {
  id: string;
  tenant_id: string;
  plan_name: string;
  type: string;
  status: string;
  mrr: number;
  modules?: string[];
  renewal_date?: string;
  started_at: string;
};

export type ModuleLicense = {
  id: string;
  tenant_id: string;
  module: string;
  display_name: string;
  status: string;
  plan?: string;
  expires_at?: string;
  granted_at: string;
};

export type FeatureFlag = {
  id: string;
  tenant_id?: string;
  key: string;
  name: string;
  scope: string;
  enabled: boolean;
  rollout_pct?: number;
  beta: boolean;
  kill_switch: boolean;
  scheduled_start?: string;
  scheduled_end?: string;
};

export type CreditLedgerEntry = {
  id: string;
  tenant_id: string;
  credit_type: string;
  balance: number;
  granted: number;
  consumed: number;
  reserved: number;
  expires_at?: string;
  updated_at: string;
};

export type UsageMeterRecord = {
  id: string;
  tenant_id: string;
  metric: string;
  quantity: number;
  period: string;
  module?: string;
  recorded_at: string;
};

export type CatalogItem = {
  id: string;
  kind: string;
  name: string;
  description: string;
  price_usd?: number;
  modules?: string[];
  features?: string[];
  region?: string;
  available: boolean;
};

export type WhiteLabelConfig = {
  id: string;
  tenant_id: string;
  partner_name: string;
  custom_domain?: string;
  logo_url?: string;
  theme_pack?: string;
  enabled: boolean;
};

export type CommercialHealth = {
  tenant_id: string;
  commercial_health_score: number;
  mrr: number;
  arr: number;
  credit_consumption_pct: number;
  feature_adoption_pct: number;
  module_adoption_pct: number;
  license_utilization_pct: number;
  active_subscriptions: number;
  active_licenses: number;
  enabled_features: number;
  validated_at: string;
};

export type ExtensionPoint = {
  dev_id: string;
  name: string;
  description: string;
};

export type CommercialGovernanceDashboard = {
  tenant_id: string;
  health: CommercialHealth;
  subscriptions: number;
  licenses: number;
  features: number;
  credit_types: number;
  usage_metrics: number;
  catalog_items: number;
  white_label_partners: number;
  extension_points: ExtensionPoint[];
  generated_at: string;
};

export type ActivateRequest = {
  module?: string;
  feature?: string;
  plan?: string;
};

export type ActivateResult = {
  activated: boolean;
  target: string;
  status: string;
  message: string;
};

const COMMERCIAL_BASE = import.meta.env.VITE_COMMERCIAL_API ?? '/api/v1/commercial';

function fetchCommercial<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${COMMERCIAL_BASE}${path}`, init);
}

export const commercialGovernanceApi = {
  listSubscriptions: () =>
    fetchCommercial<SubscriptionRecord[]>('/subscriptions'),

  listLicenses: () =>
    fetchCommercial<{ licenses: ModuleLicense[] }>('/licenses').then((r) => r.licenses),

  listFeatures: () =>
    fetchCommercial<{ features: FeatureFlag[] }>('/features').then((r) => r.features),

  listCredits: () =>
    fetchCommercial<{ credits: CreditLedgerEntry[] }>('/credits').then((r) => r.credits),

  listUsage: () =>
    fetchCommercial<UsageMeterRecord[]>('/usage'),

  listCatalog: () =>
    fetchCommercial<{ catalog: CatalogItem[] }>('/catalog').then((r) => r.catalog),

  listWhiteLabel: () =>
    fetchCommercial<{ white_label: WhiteLabelConfig[] }>('/white-label').then((r) => r.white_label),

  health: () => fetchCommercial<CommercialHealth>('/health'),

  dashboard: () => fetchCommercial<CommercialGovernanceDashboard>('/governance/dashboard'),

  activate: (body: ActivateRequest) =>
    fetchCommercial<ActivateResult>('/activate', { method: 'POST', body: JSON.stringify(body) }),

  deactivate: (body: ActivateRequest) =>
    fetchCommercial<ActivateResult>('/deactivate', { method: 'POST', body: JSON.stringify(body) }),

  extensionPoints: () =>
    fetchCommercial<{ extension_points: ExtensionPoint[] }>('/extensions').then((r) => r.extension_points),
};
